import clsx from 'clsx'
import { ReactNode, useId, useState } from 'react'
import { IconPaths } from '../../../_metronic/helpers'
import { Button } from '../Button/Button'
import { Label } from '../Label/Label'
import { ErrorLabel } from '../Label/ErrorLabel'
import styles from './styles/TableFilters.module.css'

type TableFiltersProps = {
  /** Campos principais (<TableFilter>), sempre visíveis. */
  children: ReactNode
  /**
   * Campos secundários (<TableFilter>), atrás do botão "Mais filtros". Use quando a tela tem
   * 4+ filtros: fica a busca + os mais usados à mostra, o resto recolhido.
   */
  moreFilters?: ReactNode
  /** Quantos filtros secundários estão aplicados ("Mais filtros (2)"). Abre já expandido se > 0. */
  moreFiltersActiveCount?: number
  /** Área fixa à direita (ex.: "Adicionar" ou barra de seleção em lote). */
  actions?: ReactNode
  /** Conteúdo abaixo dos campos, largura total (ex.: <ActiveFiltersBar>). */
  footer?: ReactNode
  /**
   * `inline` (padrão): campos, botão "Filtros" e ações na mesma linha.
   * `stacked`: os campos principais ocupam a 1ª linha inteira (cabem 4 lado a lado) e o botão
   * "Filtros" desce para uma 2ª linha, junto com as ações. Use quando a tela tem 4 filtros
   * principais ou várias ações.
   */
  layout?: 'inline' | 'stacked'
}

/**
 * Barra de filtros da listagem. Os campos ficam num grid de colunas iguais que quebra linha
 * inteira (sem campo "sobrando"); as ações ficam numa área própria à direita, alinhadas ao
 * pé dos campos. Em telas estreitas as ações sobem para o topo, largura total.
 *
 * @example
 * <TableFilters
 *   actions={<BotoesDaTela />}
 *   moreFilters={<TableFilter label='Cargo'>…</TableFilter>}
 *   moreFiltersActiveCount={filters.cargo ? 1 : 0}
 *   footer={<ActiveFiltersBar … />}
 * >
 *   <TableFilter label='Funcionário' wide><Input … /></TableFilter>
 * </TableFilters>
 */
export const TableFilters = ({
  children,
  moreFilters,
  moreFiltersActiveCount = 0,
  actions,
  footer,
  layout = 'inline',
}: TableFiltersProps) => {
  const moreId = useId()
  // Abre expandido se algum filtro secundário já estiver aplicado (nada fica escondido).
  const [showMore, setShowMore] = useState(moreFiltersActiveCount > 0)
  // Durante a transição a linha recorta o conteúdo (overflow: hidden); aberta, não recorta,
  // para o menu dos selects não ser cortado.
  const [animating, setAnimating] = useState(false)
  const toggleMore = () => {
    setAnimating(true)
    setShowMore((prev) => !prev)
  }
  // Rótulo curto ("+ Filtro"); o nome acessível diz o que o botão faz e o estado.
  const moreLabel = moreFiltersActiveCount > 0 ? `Filtros (${moreFiltersActiveCount})` : 'Filtros'
  const moreAriaLabel = `${showMore ? 'Ocultar' : 'Mostrar'} mais filtros${
    moreFiltersActiveCount > 0 ? `, ${moreFiltersActiveCount} aplicado(s)` : ''
  }`

  const moreToggle = moreFilters && (
    <div className={styles.filterMoreToggle}>
      <Button
        variant='secondary-outline'
        icon={showMore ? IconPaths.Remove : IconPaths.Add}
        onClick={toggleMore}
        ariaExpanded={showMore}
        ariaControls={moreId}
        ariaLabel={moreAriaLabel}
        title={moreAriaLabel}
        size='btn-sm'
      >
        {moreLabel}
      </Button>
    </div>
  )
  const actionsArea = actions && <div className={styles.filterActions}>{actions}</div>

  return (
    <div className={styles.filtersRoot}>
      {layout === 'stacked' ? (
        <>
          {/* 1ª linha: só os campos principais, largura total. */}
          <div className={clsx(styles.filters, styles.filtersStacked)}>
            <div className={styles.filterFields}>{children}</div>
          </div>
          {/* 2ª linha: botão "Filtros" à esquerda, ações à direita. */}
          {(moreToggle || actionsArea) && (
            <div className={styles.filterToolbar}>
              {moreToggle}
              {actionsArea}
            </div>
          )}
        </>
      ) : (
        /* 1ª linha: campos principais + botão "Filtros"; ações no canto direito. Os campos
           encolhem antes de quebrar. */
        <div className={styles.filters}>
          <div className={styles.filterFields}>
            {children}
            {moreToggle}
          </div>
          {actionsArea}
        </div>
      )}
      {/* 2ª linha: todos os filtros secundários, com transição de altura/opacidade. Fechada,
          fica com visibility: hidden (fora do tab e do leitor de tela). */}
      {moreFilters && (
        <div
          id={moreId}
          className={clsx(styles.filterMore, showMore && styles.filterMoreOpen)}
          onTransitionEnd={(e) => {
            if (e.target === e.currentTarget && e.propertyName === 'grid-template-rows') {
              setAnimating(false)
            }
          }}
        >
          <div className={clsx(styles.filterMoreInner, animating && styles.filterMoreClip)}>
            <div className={styles.filterFields}>{moreFilters}</div>
          </div>
        </div>
      )}
      {footer}
    </div>
  )
}

type TableFilterProps = {
  label: ReactNode
  children: ReactNode
  /** Ocupa duas colunas do grid (use no campo de busca principal). */
  wide?: boolean
  htmlFor?: string
  /**
   * Mensagem de validação abaixo do campo, sem aumentar a altura dele (não desalinha a linha
   * de filtros). Use no lugar de um <ErrorLabel> solto dentro do filtro.
   */
  error?: ReactNode
  /** Explicação curta do filtro, num ícone de ajuda ao lado do rótulo (`Label` `infoText`). */
  hint?: string
}

export const TableFilter = ({ label, children, wide, htmlFor, error, hint }: TableFilterProps) => (
  <div className={clsx(styles.filterField, wide && styles.filterFieldWide)}>
    <Label className='pb-2' htmlFor={htmlFor} infoText={hint}>
      {label}
    </Label>
    {children}
    {error && (
      <div className={styles.filterError}>
        <ErrorLabel>{error}</ErrorLabel>
      </div>
    )}
  </div>
)
